import { getType } from '@anm/helpers/saga/typesafe-actions';
import { Reducer } from 'redux';

import { initialVideoFolderState } from '..';
import {
  addProject,
  cancelCreateProject,
  clearCreatedProject,
  createProjectAsync,
  removeProjectFromList,
  updateCreatedProject,
  updateProjectPreview
} from '../../actions';
import {
  addNewProject,
  findAndUpdateFolder,
  findAndUpdateProject,
  removeProject,
  removeProjectByHostId,
  removeProjectFromFolderTree
} from '../../helpers';
import { defaultVideoInfo } from '../../saga/projectSaga';
import { VideoFolderActions } from '../../types';

const createProjectReducer: Reducer<typeof initialVideoFolderState, VideoFolderActions> = (
  state = initialVideoFolderState,
  action
) => {
  switch (action.type) {
    case getType(createProjectAsync.request): {
      const params = action.payload;

      return {
        ...state,
        createProject: {
          ...state.createProject,
          params,
          isError: false,
          isPending: true
        }
      };
    }
    case getType(addProject): {
      const newProject = action.payload;
      const { width, height, ...otherProjectData } = newProject;

      return {
        ...state,
        data: addNewProject(state.data!, newProject, newProject.folderId),
        folderTree: {
          ...state.folderTree,
          data: findAndUpdateFolder(
            state.folderTree.data!,
            newProject.folderId
          )(({ projectsCount = 0, ...other }) => ({
            ...other,
            projectsCount: projectsCount + 1
          }))
        },
        createProject: {
          ...state.createProject,
          project: {
            ...(width !== defaultVideoInfo.width && { width }),
            ...(height !== defaultVideoInfo.height && { height }),
            ...otherProjectData
          }
        }
      };
    }

    case getType(updateCreatedProject): {
      const { id, preview, projectId, shouldUpdatePreview = false } = action.payload;

      return {
        ...state,
        data: findAndUpdateProject(
          state.data!,
          id
        )(v => ({
          ...v,
          shouldUpdatePreview,
          ...(projectId && { id: projectId }),
          ...(preview && { preview_metadata: [preview] })
        }))
      };
    }

    case getType(updateProjectPreview): {
      const { id, width, height, preview } = action.payload;

      return {
        ...state,
        data: findAndUpdateProject(
          state.data!,
          id
        )(v => ({
          ...v,
          width,
          height,
          shouldUpdatePreview: false,
          preview_metadata: [preview]
        }))
      };
    }

    case getType(cancelCreateProject): {
      const projectId = action.payload;

      const { id: currentFolderId } = state.data!;

      return {
        ...state,
        createProject: {
          ...state.createProject,
          cancelUploadProjectId: projectId
        },
        data: removeProject(state.data!, projectId!),
        folderTree: removeProjectFromFolderTree({ state, currentFolderId })
      };
    }

    case getType(clearCreatedProject): {
      return {
        ...state,
        createProject: {
          data: null,
          project: null,
          isError: false,
          isPending: false,
          cancelUploadProjectId: null
        }
      };
    }

    case getType(removeProjectFromList): {
      const projectId = action.payload;
      const { id: currentFolderId } = state.data!;

      return {
        ...state,
        data: removeProject(state.data!, projectId!),
        folderTree: removeProjectFromFolderTree({ state, currentFolderId })
      };
    }

    case getType(createProjectAsync.success): {
      const response = action.payload;
      const { id } = response;

      return {
        ...state,
        createProject: {
          ...state.createProject,
          data: { id },
          isPending: false,
          isError: false,
          error: null
        }
      };
    }
    case getType(createProjectAsync.failure): {
      const error = action.payload;
      const hostId = state.createProject.params?.hostingId;
      const { id: currentFolderId } = state.data!;

      return {
        ...state,
        data: removeProjectByHostId(state.data!, hostId!),
        folderTree: removeProjectFromFolderTree({ state, currentFolderId }),
        createProject: {
          ...state.createProject,
          error,
          isPending: false,
          isError: true
        }
      };
    }
    default:
      return state;
  }
};

export default createProjectReducer;
